import { useEffect, useState } from "react";
import { api } from "../lib/api";
import useSeo from "../hooks/useSeo";

export default function About() {
  const [page, setPage] = useState(null);
  useSeo({
    title: "About saamne — in-person experiences across India",
    description:
      "saamne is a startup running in-person experiences (only) across Hyderabad, Bangalore and Pune — coffee tastings, watch parties, nature walks, senior getaways and lifeskill workshops.",
    path: "/about",
  });

  useEffect(() => {
    api.get("/pages/about")
      .then((r) => setPage(r.data))
      .catch(() => setPage({
        title: "Why saamne?",
        body: "An admin needs to set up the About page from /admin → About.",
      }));
  }, []);

  if (!page) return <div className="max-w-3xl mx-auto px-6 pt-16" data-testid="about-loading">Loading…</div>;

  return (
    <div data-testid="about-page" className="max-w-3xl mx-auto px-6 md:px-10 pt-16 pb-24">
      <div className="label-eyebrow">About</div>
      <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-tight">{page.title}</h1>
      <div className="mt-8 space-y-6 text-lg leading-relaxed whitespace-pre-line" style={{ color: "var(--text-main)" }}>
        {page.body}
      </div>

      <section className="mt-14 space-y-5 text-base leading-relaxed" style={{ color: "var(--text-main)" }}>
        <h2 className="font-serif text-3xl md:text-4xl leading-tight">What we host</h2>
        <p>
          We host five kinds of gatherings, all in person, all small enough that
          everyone gets to actually talk. <strong>Coffee tastings</strong> are
          unhurried morning circles — three origins, a few brewing methods,
          and conversation that drifts from beans to the people who grew them.
          <strong> Watch parties</strong> are old and new films projected on a
          wall, with phones in a basket at the door and a long conversation
          after the credits. <strong>Nature walks</strong> are slow morning
          routes through old gardens, lakes, and quiet city forests — pace set
          by the slowest one in the group.
        </p>
        <p>
          <strong>Senior getaways</strong> are designed for elders — gentle
          itineraries with benches every few minutes, chai breaks, and story
          circles. Family members are welcome, but the day belongs to the
          seniors. <strong>Lifeskill building</strong> are short, hands-on
          workshops on the things our schools forgot to teach — fixing a flat
          tyre, cleaning your own AC, sorting your personal finances, writing
          a will, basic plumbing, repairing a fan.
        </p>

        <h2 className="font-serif text-3xl md:text-4xl leading-tight pt-4">Where we host</h2>
        <p>
          We are starting small, on purpose. For now, gatherings happen across{" "}
          <strong>Hyderabad</strong>, <strong>Bangalore</strong> and{" "}
          <strong>Pune</strong>. If a theme finds enough yeses in a particular
          city, we host it there first. We will expand to other Indian cities
          once the first round of gatherings is well-loved.
        </p>

        <h2 className="font-serif text-3xl md:text-4xl leading-tight pt-4">How it works</h2>
        <p>
          There is no app, no membership, no mailing list. You{" "}
          <a href="/#rsvp" className="underline" style={{ color: "var(--primary)" }}>RSVP your interest</a>{" "}
          for a theme and a city. When we host one near you, we reach out on
          the contact you've left with us — venue, timing, and any specifics
          shared one-to-one, never posted publicly. You show up. You leave
          when you want to.
        </p>

        <h2 className="font-serif text-3xl md:text-4xl leading-tight pt-4">Why this matters</h2>
        <p>
          The internet was supposed to bring us closer. In some ways it did.
          In most other ways, it quietly replaced our muscles for showing up
          — for sitting through awkward silences, for hosting six people in a
          small flat, for letting a long evening go nowhere in particular.
          saamne is a startup to put those muscles back to work.
        </p>
      </section>

      <div className="mt-14 card-warm p-8">
        <div className="label-eyebrow">Want to host?</div>
        <p className="font-serif text-2xl mt-3 leading-snug">
          A room, a recipe, a film you love — write to <a href="mailto:hello@saamne.com" className="underline" style={{ color: "var(--primary)" }}>hello@saamne.com</a>.
        </p>
      </div>
    </div>
  );
}
